import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { PlusIcon, XMarkIcon, Bars3Icon } from "@heroicons/react/24/outline";

import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { apiService } from "../../services/api";

const RatingScaleForm = ({ clubId, editingScale, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: "",
    scale_type: "numeric",
    min_value: 1,
    max_value: 10,
    increment: 1,
    custom_options: ["Excellent", "Good", "Average", "Below Average", "Poor"],
    show_decimals: false,
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [newOption, setNewOption] = useState("");
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    if (editingScale) {
      setFormData({
        name: editingScale.name || "",
        scale_type: editingScale.scale_type || "numeric",
        min_value: editingScale.min_value || 1,
        max_value: editingScale.max_value || 10,
        increment: editingScale.increment || 1,
        custom_options: editingScale.custom_options || [
          "Excellent",
          "Good",
          "Average",
          "Below Average",
          "Poor",
        ],
        show_decimals: editingScale.show_decimals || false,
        description: editingScale.description || "",
      });
    }
  }, [editingScale]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        club_id: clubId,
        min_value:
          formData.scale_type === "numeric"
            ? parseFloat(formData.min_value)
            : null,
        max_value:
          formData.scale_type === "numeric"
            ? parseFloat(formData.max_value)
            : null,
        increment:
          formData.scale_type === "numeric"
            ? parseFloat(formData.increment)
            : null,
        custom_options:
          formData.scale_type === "custom_options"
            ? formData.custom_options
            : null,
      };

      if (editingScale) {
        await apiService.updateRatingScale(editingScale.id, submitData);
        toast.success("Rating scale updated successfully!");
      } else {
        await apiService.createRatingScale(submitData);
        toast.success("Rating scale created successfully!");
      }

      onSuccess();
    } catch (error) {
      toast.error("Failed to save rating scale");
    }
    setLoading(false);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addCustomOption = () => {
    if (newOption.trim()) {
      setFormData((prev) => ({
        ...prev,
        custom_options: [...prev.custom_options, newOption.trim()],
      }));
      setNewOption("");
    }
  };

  const removeCustomOption = (index) => {
    setFormData((prev) => ({
      ...prev,
      custom_options: prev.custom_options.filter((_, i) => i !== index),
    }));
  };

  const moveOption = (index, direction) => {
    const newOptions = [...formData.custom_options];
    const newIndex = direction === "up" ? index - 1 : index + 1;

    if (newIndex >= 0 && newIndex < newOptions.length) {
      [newOptions[index], newOptions[newIndex]] = [
        newOptions[newIndex],
        newOptions[index],
      ];
      setFormData((prev) => ({ ...prev, custom_options: newOptions }));
    }
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", index);
    e.dataTransfer.effectAllowed = "move";
    setDraggedIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData("text/plain"));

    if (dragIndex !== dropIndex) {
      const newOptions = [...formData.custom_options];
      const draggedItem = newOptions[dragIndex];

      // Remove the dragged item
      newOptions.splice(dragIndex, 1);

      // Insert it at the new position
      newOptions.splice(dropIndex, 0, draggedItem);

      setFormData((prev) => ({ ...prev, custom_options: newOptions }));
    }
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const getPreviewText = () => {
    switch (formData.scale_type) {
      case "numeric":
        const examples = [];
        for (
          let i = 0;
          i < 4 &&
          formData.min_value + i * formData.increment <= formData.max_value;
          i++
        ) {
          examples.push(formData.min_value + i * formData.increment);
        }
        return `Example values: ${examples.join(", ")}${
          examples.length < 4 ? "" : "..."
        }`;
      case "pass_fail":
        return "Options: Pass, Fail";
      case "custom_options":
        return `Options: ${formData.custom_options.join(", ")}`;
      default:
        return "";
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Input
        label="Scale Name"
        placeholder="e.g., Ground Ball Recovery Scale, Standard 1-10"
        value={formData.name}
        onChange={(e) => handleInputChange("name", e.target.value)}
        required
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Scale Type
        </label>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
          value={formData.scale_type}
          onChange={(e) => handleInputChange("scale_type", e.target.value)}
        >
          <option value="numeric">Numeric Scale</option>
          <option value="pass_fail">Pass/Fail</option>
          <option value="custom_options">Custom Options</option>
        </select>
      </div>

      {formData.scale_type === "numeric" && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Minimum Value"
              type="number"
              step="0.01"
              value={formData.min_value}
              onChange={(e) =>
                handleInputChange("min_value", parseFloat(e.target.value))
              }
              required
            />
            <Input
              label="Maximum Value"
              type="number"
              step="0.01"
              value={formData.max_value}
              onChange={(e) =>
                handleInputChange("max_value", parseFloat(e.target.value))
              }
              required
            />
            <Input
              label="Increment"
              type="number"
              step="0.01"
              placeholder="e.g., 0.5, 1, 0.25"
              value={formData.increment}
              onChange={(e) =>
                handleInputChange("increment", parseFloat(e.target.value))
              }
              required
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="show_decimals"
              checked={formData.show_decimals}
              onChange={(e) =>
                handleInputChange("show_decimals", e.target.checked)
              }
              className="h-4 w-4 text-slate-600 focus:ring-slate-500 border-gray-300 rounded"
            />
            <label
              htmlFor="show_decimals"
              className="ml-2 block text-sm text-gray-700"
            >
              Show decimal places in mobile app
            </label>
          </div>
        </div>
      )}

      {formData.scale_type === "custom_options" && (
        <div className="space-y-4">
          <div className="flex space-x-2">
            <Input
              placeholder="Add new option (e.g., Excellent, Good)"
              value={newOption}
              onChange={(e) => setNewOption(e.target.value)}
              onKeyPress={(e) =>
                e.key === "Enter" && (e.preventDefault(), addCustomOption())
              }
              className="flex-1"
            />
            <Button
              type="button"
              onClick={addCustomOption}
              disabled={!newOption.trim()}
            >
              <PlusIcon className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Options (drag to reorder):
            </label>
            {formData.custom_options.map((option, index) => (
              <div
                key={index}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-center space-x-2 p-2 rounded-md transition-all duration-150 cursor-move border ${
                  draggedIndex === index
                    ? "bg-slate-100 border-slate-300 opacity-50"
                    : "bg-gray-50 border-transparent hover:bg-gray-100 hover:border-gray-300"
                }`}
              >
                <Bars3Icon className="w-4 h-4 text-gray-400 cursor-grab active:cursor-grabbing" />
                <div className="flex flex-col space-y-1">
                  <button
                    type="button"
                    onClick={() => moveOption(index, "up")}
                    disabled={index === 0}
                    className="text-xs text-slate-600 disabled:text-gray-400 hover:text-slate-800"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => moveOption(index, "down")}
                    disabled={index === formData.custom_options.length - 1}
                    className="text-xs text-slate-600 disabled:text-gray-400 hover:text-slate-800"
                  >
                    ↓
                  </button>
                </div>
                <span className="flex-1 text-sm select-none">{option}</span>
                <button
                  type="button"
                  onClick={() => removeCustomOption(index)}
                  className="text-red-600 hover:text-red-800 transition-colors duration-150"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description (Optional)
        </label>
        <textarea
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
          rows="3"
          placeholder="Explain how this scale should be used..."
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
        />
      </div>

      {/* Preview */}
      <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
        <h4 className="text-sm font-medium text-slate-900 mb-2">Preview</h4>
        <p className="text-sm text-slate-800">{getPreviewText()}</p>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading
            ? "Saving..."
            : editingScale
              ? "Update Scale"
              : "Create Scale"}
        </Button>
      </div>
    </form>
  );
};

export default RatingScaleForm;
